import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import { getPendingLeaves, getEditTickets, getWfhPeriods } from "../api/attendanceService";
import useChatStore from "../store/chatStore";
import useAuthStore from "../store/authStore";

function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(collapsed));
    window.dispatchEvent(new CustomEvent("sidebar_toggle", { detail: { collapsed } }));
  }, [collapsed]);
  const [pendingLeavesCount, setPendingLeavesCount] = useState(
    Number(sessionStorage.getItem("pending_leaves_count") || 0)
  );
  const [overrideRequestCount, setOverrideRequestCount] = useState(
    Number(sessionStorage.getItem("override_request_count") || 0)
  );
  const profilePhoto = useAuthStore(state => state.profilePhoto);
  const navigate = useNavigate();

  const unreadCounts = useChatStore(state => state.unreadCounts || {});
  const totalUnreadChats = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // Close mobile drawer on route change
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── PROFILE PHOTO ── */
  useEffect(() => {
    (async () => {
      try {
        const id = useAuthStore.getState().employeeId;
        if (!id) return;
        const res = await apiClient.get(`/accounts/api/users/${id}/`);
        const user = res.data.user;
        if (user?.photo) {
          const p = user.photo.startsWith("http") ? user.photo
            : user.photo.startsWith("/") ? user.photo
              : `/media/${user.photo}`;
          useAuthStore.getState().setProfilePhoto(p);
        }
      } catch { /* silent */ }
    })();
  }, []);

  /* ── PENDING LEAVES ── */
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getPendingLeaves();
        const leaves = res.data.leaves || res.data.leave_data || res.data.results || [];
        const count = leaves.filter(l => l.final_status === "Pending" || l.status === "Pending").length;
        sessionStorage.setItem("pending_leaves_count", count);
        setPendingLeavesCount(count);
      } catch { setPendingLeavesCount(0); }
    };
    fetch();
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, []);

  /* ── EMPLOYEE NOTIFS ── */
  useEffect(() => {
    const fetch = async () => {
      try {
        let total = 0;
        // Fetch in parallel using Promise.all (Performance Optimization)
        const [tRes, rRes, wRes, hwRes] = await Promise.all([
          getEditTickets({ status: "PENDING" }),
          apiClient.get("/attendance/api/remote-attendance/"),
          getWfhPeriods({ status: "PENDING" }),
          // Use apiClient directly to bypass dedupGet cache for real-time count
          apiClient.get("/attendance/api/holiday-work-requests/", { params: { status: "PENDING" } }),
        ]);
        const tickets = tRes.data.tickets || [];
        total += tickets.filter(t => t.request_type === "ATTENDANCE" || t.request_type === "LEAVE").length;
        total += (rRes.data.remote_requests || []).length;
        total += (wRes.data.wfh_periods || []).length;

        // Holiday work request count for Allow Check-IN badge
        const hwCount = hwRes.data.pending_count || 0;
        sessionStorage.setItem("override_request_count", hwCount);
        setOverrideRequestCount(hwCount);

        sessionStorage.setItem("employee_notif_count", total);
      } catch { /* silent */ }
    };
    fetch();
    const id = setInterval(fetch, 60000);

    // Instantly refresh badge when admin acts on a holiday work request
    const onHwrUpdated = () => fetch();
    window.addEventListener("holiday-work-request-updated", onHwrUpdated);

    return () => {
      clearInterval(id);
      window.removeEventListener("holiday-work-request-updated", onHwrUpdated);
    };
  }, []);

  /* ── Chat Unread Counts Sync ── */
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await apiClient.get("/chat/api/messages/unread-counts/");
        if (res.data?.unread_counts) {
          useChatStore.getState().setUnreadCounts(res.data.unread_counts);
        }
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000); // sync every 10s
    return () => clearInterval(id);
  }, []);

  /* ── LOGOUT ── */
  const handleLogout = async () => {
    try {
      await apiClient.post("/accounts/api/logout/");
    } catch { /* silent */ }
    useAuthStore.getState().clearAuth();
    navigate("/login");
  };

  const sidebarContent = (isMobile = false) => (
    <aside className={`
      flex flex-col h-full
      bg-gradient-to-b from-white/10 via-white/5 to-transparent
      backdrop-blur-md border-r border-white/20 text-white shadow-2xl
      transition-all duration-500 ease-in-out
      ${isMobile ? "w-72" : collapsed ? "w-20" : "w-72"}
    `}>
      {/* AVATAR */}
      <div className="flex flex-col items-center pt-8 pb-6">
        <div
          onClick={() => !isMobile && setCollapsed(c => !c)}
          className={`
            rounded-full border-2 border-white/30
            bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500
            overflow-hidden shadow-lg hover:shadow-2xl
            transition-all duration-500 hover:scale-105 hover:border-white/50
            ${isMobile ? "w-28 h-28" : collapsed ? "w-14 h-14 cursor-pointer" : "w-28 h-28 cursor-pointer"}
          `}
        >
          <img src={profilePhoto} alt="Admin" className="w-full h-full object-cover"
            onError={e => { e.target.src = "/images/avatar.png"; }} />
        </div>
        {(isMobile || !collapsed) && (
          <div className="mt-4 text-center">
            <p className="text-lg font-semibold text-white tracking-wide">Admin Panel</p>
            <p className="text-xs text-white/70 mt-1">Management System</p>
          </div>
        )}
      </div>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

      {/* NAV */}
      <nav className="flex flex-col gap-1.5 px-3 py-5 flex-1 overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden">
        <SLink to="/admin/dashboard" label="Dashboard" collapsed={!isMobile && collapsed}><HomeIcon /></SLink>
        {/* <SLink to="/admin/create-user" label="Create New Employee" collapsed={!isMobile && collapsed}><UserPlusIcon /></SLink> */}
        {/* <SLink to="/admin/users" label="View All Employees" collapsed={!isMobile && collapsed}
          badge={employeeNotifCount} badgeColor="cyan"
          matchPaths={["/admin/user-details", "/admin/update-user"]}><UsersIcon /></SLink> */}
        <SLink to="/admin/projects" label="View Project List" collapsed={!isMobile && collapsed}><ChartIcon /></SLink>
        <SLink to="/admin/pending-leave-list" label="Pending Leaves" collapsed={!isMobile && collapsed}
          badge={pendingLeavesCount} badgeColor="red"><LeaveIcon /></SLink>
        <SLink to="/admin/allow-over-ride" label="Allow Check-IN" collapsed={!isMobile && collapsed}
          badge={overrideRequestCount} badgeColor="amber"><CheckInIcon /></SLink>
        <SLink to="/admin/manage-calendar" label="Editorial Calendar" collapsed={!isMobile && collapsed}><CalendarIcon /></SLink>
        <SLink to="/admin/add-leave-policy" label="Add CL Policy" collapsed={!isMobile && collapsed}><PolicyIcon /></SLink>
        <SLink to="/admin/chat" label="Messages" collapsed={!isMobile && collapsed}
          badge={totalUnreadChats} badgeColor="emerald"><ChatIcon /></SLink>
        <SLink to="/admin/send-mail" label="Send Mail" collapsed={!isMobile && collapsed}><MailIcon /></SLink>
        <SLink
          to="/admin/team-review"
          label="Team Review"
          collapsed={!isMobile && collapsed}
          customActive={(loc) => {
            return loc.pathname === "/admin/team-review" ||
              loc.pathname.startsWith("/employee/assigned-projects") ||
              loc.pathname.startsWith("/employee/progress") ||
              loc.pathname.startsWith("/manager/activity") ||
              loc.pathname.startsWith("/manager/activity/progress");
          }}
        >
          <UsersIcon />
        </SLink>
      </nav>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

      {/* FOOTER */}
      <div className="p-3 flex flex-col gap-2">
        <button onClick={handleLogout}
          className="flex items-center justify-center gap-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-sm font-medium py-3 rounded-xl shadow-lg transition-all duration-300 active:scale-95">
          <LogoutIcon />
          {(isMobile || !collapsed) && <span>Log Out</span>}
        </button>
        <button onClick={() => navigate("/reset-password")}
          className="flex items-center justify-center gap-3 border-2 border-white/40 text-sm font-medium py-3 rounded-xl hover:bg-white/10 transition-all duration-300 active:scale-95">
          <LockIcon />
          {(isMobile || !collapsed) && <span>Reset Password</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      {/* ── MOBILE HAMBURGER ── */}
      <button
        onClick={() => setMobileOpen(o => !o)}
        className="md:hidden fixed top-4 left-4 z-[60] w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-md border border-white/20 text-white shadow-lg"
        aria-label="Toggle menu"
      >
        {mobileOpen
          ? <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
          : <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        }
      </button>

      {/* ── MOBILE OVERLAY + DRAWER ── */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="md:hidden fixed top-0 left-0 h-screen z-[56] overflow-hidden">
            {sidebarContent(true)}
          </div>
        </>
      )}

      {/* ── DESKTOP SIDEBAR ── */}
      <div className={`hidden md:block fixed top-0 left-0 h-screen z-50 transition-all duration-500 ${collapsed ? "w-20" : "w-72"}`}>
        {sidebarContent(false)}
      </div>
    </>
  );
}

/* ── SIDEBAR LINK ── */
function SLink({ to, label, children, collapsed, badge, badgeColor = "red", matchPaths = [], customActive }) {
  const location = useLocation();
  const isActive = customActive
    ? customActive(location)
    : (location.pathname === to
      || location.pathname.startsWith(to + "/")
      || matchPaths.some(p => location.pathname.startsWith(p)));

  const dotBg = { red: "bg-red-500", cyan: "bg-cyan-400", yellow: "bg-yellow-400", emerald: "bg-emerald-400" }[badgeColor] || "bg-red-500";
  const pillBg = { red: "bg-red-500", cyan: "bg-cyan-500", yellow: "bg-yellow-500", emerald: "bg-emerald-500" }[badgeColor] || "bg-red-500";
  const pillTxt = badgeColor === "yellow" ? "text-slate-900" : "text-white";

  return (
    <Link to={to} className={`
      flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
      transition-all duration-200 relative active:scale-95
      ${isActive ? "bg-white/25 shadow-lg border border-white/30" : "hover:bg-white/15"}
      ${collapsed ? "justify-center" : ""}
    `}>
      <div className="relative flex-shrink-0">
        {children}
        {badge > 0 && collapsed && (
          <span className={`absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white ${dotBg} rounded-full border border-white/30 animate-pulse`}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      {!collapsed && <span className="whitespace-nowrap flex-1 truncate">{label}</span>}
      {badge > 0 && !collapsed && (
        <span className={`ml-auto flex items-center justify-center min-w-[22px] h-5 px-1.5 text-xs font-bold ${pillTxt} ${pillBg} rounded-full animate-pulse`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

/* ── ICONS ── */
const HomeIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" /></svg>;
const UsersIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
const ChartIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
const CalendarIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
const CheckInIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 4 12 14.01 9 11.01" /></svg>;
const LeaveIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="10" y1="16" x2="14" y2="16" /></svg>;
const PolicyIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const LogoutIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;
const LockIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
const ChatIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
const MailIcon = () => <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>;

export default AdminSidebar;